import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Search, Calendar, Users, Info } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { useActiveEvents } from '../../hooks/useConvex';
import { Event } from '../../types';
import { BackButton } from '../../components/ui/BackButton';
import { getCategoryColor, sortEventsByTime } from '../../utils/eventUtils';

export const EventsPage: React.FC = () => {
    const eventsData = useActiveEvents();
    const loading = eventsData === undefined;

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

    // Transform Convex data to Event type
    const events = useMemo(() => {
        if (!eventsData) return [];
        return eventsData.map((e: Record<string, unknown>) => ({
            ...e,
            event_id: e._id,
        })) as Event[];
    }, [eventsData]);

    const filteredEvents = useMemo(() => {
        let filtered = events;

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(event => event.category === selectedCategory);
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(event =>
                event.name.toLowerCase().includes(query) ||
                event.description?.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [searchQuery, selectedCategory, events]);

    const categories = ['all', ...Array.from(new Set(events.map(e => e.category)))];

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] flex justify-center items-center">
                <Loader size="lg" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-12 px-4 md:px-8">
            <div className="max-w-7xl mx-auto space-y-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <BackButton className="mb-4" />
                        <h1 className="text-4xl md:text-5xl font-display font-bold text-white">
                            Event <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#7C4DFF]">Showcase</span>
                        </h1>
                        <p className="text-white/60 mt-2 text-lg">Explore the challenges that await you at Enigma 2026.</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row justify-center items-center gap-4 max-w-4xl mx-auto">
                    <div className="w-full md:w-1/2">
                        <Input
                            placeholder="Search events..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            leftIcon={<Search className="w-5 h-5 text-white/40" />}
                            fullWidth
                            className="bg-black/50 backdrop-blur-xl border-white/10 focus:border-[#FF991C]/50"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 w-full md:w-auto custom-scrollbar">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all whitespace-nowrap ${selectedCategory === category
                                    ? 'bg-[#FF991C] text-black shadow-[0_0_15px_rgba(255,153,28,0.4)]'
                                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Events Grid */}
                {filteredEvents.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
                            <Search className="w-10 h-10 text-white/40" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">No events found</h3>
                        <p className="text-white/60">Try adjusting your search or filters.</p>
                    </div>
                ) : (
                    <div className="space-y-16">
                        {/* Day 1 Events */}
                        {sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 1'), 'day1').length > 0 && (
                            <div>
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="h-px bg-gradient-to-r from-transparent to-[#FF991C] flex-1" />
                                    <h2 className="text-2xl md:text-3xl font-display font-bold text-[#FF991C] uppercase tracking-widest text-center">Day 1 Events</h2>
                                    <div className="h-px bg-gradient-to-l from-transparent to-[#FF991C] flex-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 1'), 'day1').map(event => {
                                        const colors = getCategoryColor(event.category);
                                        return (
                                            <Card
                                                key={event.event_id}
                                                className={`group cursor-pointer bg-white/[0.03] border border-white/[0.08] rounded-2xl backdrop-blur-xl ${colors.border} transition-all duration-500 hover:-translate-y-2 flex flex-col h-full`}
                                                onClick={() => setSelectedEvent(event)}
                                            >
                                                <div className="h-48 overflow-hidden relative flex-shrink-0">
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10 opacity-80"></div>
                                                    <img
                                                        src={event.cover_image_url || event.image_url || 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?q=80&w=2069&auto=format&fit=crop'}
                                                        alt={event.name}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                    />
                                                    <div className="absolute bottom-4 left-4 z-20">
                                                        <div className="flex gap-2 mb-2">
                                                            <Badge variant="neutral" size="sm" className={`${colors.badge} backdrop-blur-md`}>
                                                                {event.category}
                                                            </Badge>
                                                        </div>
                                                        <h3 className={`text-2xl font-display font-bold text-white group-hover:${colors.text} transition-colors`}>
                                                            {event.name}
                                                        </h3>
                                                        {event.sub_event_name && (
                                                            <p className="text-sm text-white/60 font-medium mt-1">
                                                                {event.sub_event_name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-6 space-y-4 flex-1 flex flex-col">
                                                    <p className="text-white/60 line-clamp-3 text-sm leading-relaxed flex-1">
                                                        {event.description}
                                                    </p>

                                                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center text-white/60 text-sm">
                                                                <Users className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                <span>{event.min_team_size === event.max_team_size ? event.max_team_size : `${event.min_team_size}-${event.max_team_size}`} Members</span>
                                                            </div>
                                                            {event.schedule?.day1 && (
                                                                <div className="flex items-center text-white/60 text-sm">
                                                                    <Calendar className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                    <span>{event.schedule.day1.start} - {event.schedule.day1.end}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button variant="ghost" size="sm" className={`${colors.text} hover:text-white hover:${colors.bg}`}>
                                                            Details <Info className="w-4 h-4 ml-2" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Day 2 Events */}
                        {sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 2'), 'day2').length > 0 && (
                            <div>
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="h-px bg-gradient-to-r from-transparent to-[#7C4DFF] flex-1" />
                                    <h2 className="text-2xl md:text-3xl font-display font-bold text-[#7C4DFF] uppercase tracking-widest text-center">Day 2 Events</h2>
                                    <div className="h-px bg-gradient-to-l from-transparent to-[#7C4DFF] flex-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 2'), 'day2').map(event => {
                                        const colors = getCategoryColor(event.category);
                                        return (
                                            <Card
                                                key={event.event_id}
                                                className={`group cursor-pointer bg-white/[0.03] border border-white/[0.08] rounded-2xl backdrop-blur-xl ${colors.border} transition-all duration-500 hover:-translate-y-2 flex flex-col h-full`}
                                                onClick={() => setSelectedEvent(event)}
                                            >
                                                <div className="h-48 overflow-hidden relative flex-shrink-0">
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10 opacity-80"></div>
                                                    <img
                                                        src={event.cover_image_url || event.image_url || 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?q=80&w=2069&auto=format&fit=crop'}
                                                        alt={event.name}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                    />
                                                    <div className="absolute bottom-4 left-4 z-20">
                                                        <Badge variant="neutral" size="sm" className={`mb-2 ${colors.badge} backdrop-blur-md`}>
                                                            {event.category}
                                                        </Badge>
                                                        <h3 className={`text-2xl font-display font-bold text-white group-hover:${colors.text} transition-colors`}>
                                                            {event.name}
                                                        </h3>
                                                        {event.sub_event_name && (
                                                            <p className="text-sm text-white/60 font-medium mt-1">
                                                                {event.sub_event_name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-6 space-y-4 flex-1 flex flex-col">
                                                    <p className="text-white/60 line-clamp-3 text-sm leading-relaxed flex-1">
                                                        {event.description}
                                                    </p>

                                                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center text-white/60 text-sm">
                                                                <Users className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                <span>{event.min_team_size === event.max_team_size ? event.max_team_size : `${event.min_team_size}-${event.max_team_size}`} Members</span>
                                                            </div>
                                                            {event.schedule?.day2 && (
                                                                <div className="flex items-center text-white/60 text-sm">
                                                                    <Calendar className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                    <span>{event.schedule.day2.start} - {event.schedule.day2.end}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button variant="ghost" size="sm" className={`${colors.text} hover:text-white hover:${colors.bg}`}>
                                                            Details <Info className="w-4 h-4 ml-2" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Both Days Events */}
                        {sortEventsByTime(filteredEvents.filter(e => e.day === 'Both'), 'both').length > 0 && (
                            <div>
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="h-px bg-gradient-to-r from-transparent to-[#311b92] flex-1" />
                                    <h2 className="text-2xl md:text-3xl font-display font-bold text-[#311b92] uppercase tracking-widest text-center">Multi-Day Events</h2>
                                    <div className="h-px bg-gradient-to-l from-transparent to-[#311b92] flex-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {sortEventsByTime(filteredEvents.filter(e => e.day === 'Both'), 'both').map(event => {
                                        const colors = getCategoryColor(event.category);
                                        return (
                                            <Card
                                                key={event.event_id}
                                                className={`group cursor-pointer bg-white/[0.03] border border-white/[0.08] rounded-2xl backdrop-blur-xl ${colors.border} transition-all duration-500 hover:-translate-y-2 flex flex-col h-full`}
                                                onClick={() => setSelectedEvent(event)}
                                            >
                                                <div className="h-48 overflow-hidden relative flex-shrink-0">
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10 opacity-80"></div>
                                                    <img
                                                        src={event.cover_image_url || event.image_url || 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?q=80&w=2069&auto=format&fit=crop'}
                                                        alt={event.name}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                    />
                                                    <div className="absolute bottom-4 left-4 z-20">
                                                        <div className="flex gap-2 mb-2">
                                                            <Badge variant="neutral" size="sm" className={`${colors.badge} backdrop-blur-md`}>
                                                                {event.category}
                                                            </Badge>
                                                            <Badge variant="warning" size="sm" className="backdrop-blur-md">
                                                                Both Days
                                                            </Badge>
                                                        </div>
                                                        <h3 className={`text-2xl font-display font-bold text-white group-hover:${colors.text} transition-colors`}>
                                                            {event.name}
                                                        </h3>
                                                        {event.sub_event_name && (
                                                            <p className="text-sm text-white/60 font-medium mt-1">
                                                                {event.sub_event_name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-6 space-y-4 flex-1 flex flex-col">
                                                    <p className="text-white/60 line-clamp-3 text-sm leading-relaxed flex-1">
                                                        {event.description}
                                                    </p>

                                                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center text-white/60 text-sm">
                                                                <Users className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                <span>{event.min_team_size === event.max_team_size ? event.max_team_size : `${event.min_team_size}-${event.max_team_size}`} Members</span>
                                                            </div>
                                                            <div className="flex items-center text-white/60 text-sm">
                                                                <Calendar className={`w-4 h-4 mr-2 ${colors.text}`} />
                                                                <span>D1: {event.schedule?.day1?.start} | D2: {event.schedule?.day2?.start}</span>
                                                            </div>
                                                        </div>
                                                        <Button variant="ghost" size="sm" className={`${colors.text} hover:text-white hover:${colors.bg}`}>
                                                            Details <Info className="w-4 h-4 ml-2" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Event Details Modal */}
            <Modal
                isOpen={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.name || 'Event Details'}
            >
                {selectedEvent && (
                    <div className="space-y-6">
                        {selectedEvent.sub_event_name && (
                            <p className="text-xl text-[#FF991C] font-display font-bold -mt-4 mb-4">
                                {selectedEvent.sub_event_name}
                            </p>
                        )}
                        <div className="rounded-xl overflow-hidden h-64 relative">
                            <img
                                src={selectedEvent.cover_image_url || selectedEvent.image_url || 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?q=80&w=2069&auto=format&fit=crop'}
                                alt={selectedEvent.name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                            <div className="absolute bottom-4 left-4">
                                <Badge variant="neutral" className="bg-[#FF991C]/20 text-[#FF991C] border-[#FF991C]/30 backdrop-blur-md mb-2">
                                    {selectedEvent.category}
                                </Badge>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                                    <Info className="w-5 h-5 text-[#FF991C]" /> Description
                                </h3>
                                <p className="text-white/60 leading-relaxed whitespace-pre-wrap">
                                    {selectedEvent.description}
                                </p>
                            </div>

                            {selectedEvent.guidelines && (
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                                        <Info className="w-5 h-5 text-[#7C4DFF]" /> Guidelines
                                    </h3>
                                    <div className="prose prose-invert prose-sm max-w-none text-white/60">
                                        <ReactMarkdown>
                                            {selectedEvent.guidelines}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Team Size</p>
                                    <p className="text-lg font-bold text-white flex items-center gap-2">
                                        <Users className="w-5 h-5 text-[#FF991C]" />
                                        {selectedEvent.max_participants} Members
                                    </p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Schedule</p>
                                    <div className="text-sm font-bold text-white">
                                        {(selectedEvent.day === 'Day 1' || selectedEvent.day === 'Both') && selectedEvent.schedule?.day1 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#FF991C]">Day 1:</span>
                                                {selectedEvent.schedule.day1.start} - {selectedEvent.schedule.day1.end}
                                            </div>
                                        )}
                                        {(selectedEvent.day === 'Day 2' || selectedEvent.day === 'Both') && selectedEvent.schedule?.day2 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#7C4DFF]">Day 2:</span>
                                                {selectedEvent.schedule.day2.start} - {selectedEvent.schedule.day2.end}
                                            </div>
                                        )}
                                        {!selectedEvent.day && <span className="text-white/60">TBA</span>}
                                    </div>
                                </div>
                            </div>

                            {selectedEvent.event_manager && selectedEvent.event_manager.name && (
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Event Manager</p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF991C]/20 to-[#7C4DFF]/20 flex items-center justify-center border border-[#FF991C]/30">
                                            <span className="text-[#FF991C] font-bold">{selectedEvent.event_manager.name.charAt(0)}</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-bold">{selectedEvent.event_manager.name}</p>
                                            {selectedEvent.event_manager.phone && (
                                                <p className="text-sm text-white/60">{selectedEvent.event_manager.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedEvent.rules && selectedEvent.rules.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-[#EF4444]" /> Rules & Regulations
                                    </h3>
                                    <ul className="list-disc list-inside space-y-2 text-white/60">
                                        {selectedEvent.rules.map((rule, idx) => (
                                            <li key={idx}>{rule}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end pt-4 border-t border-white/10">
                            <Button variant="primary" onClick={() => setSelectedEvent(null)}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
